import { useEffect, useRef, useState } from 'react';
import type { SeekerPin, VehicleType, DurationType } from '../types';
import { VEHICLE_LABELS, DURATION_LABELS, SECURITY_LABELS } from '../types';
import { trackEvent } from '../services/analytics';
import { useTipSeen } from '../hooks/useTour';

interface SeekerFormProps {
  id: string;
  lat: number;
  lng: number;
  onSubmit: (data: Omit<SeekerPin, 'id' | 'created_at' | 'comment_count'> & { email: string; phone: string }) => Promise<void>;
}

export default function SeekerForm({ id, lat, lng, onSubmit }: SeekerFormProps) {
  const [formData, setFormData] = useState({
    lat,
    lng,
    vehicle_types: [] as VehicleType[],
    duration_needed: 'monthly' as DurationType,
    wants_free: false,
    budget_min: '',
    budget_max: '',
    requires_covered: false,
    requires_security: [] as string[],
    requires_24x7: false,
    requires_ev: false,
    email: '',
    phone: '',
  });
  const [error, setError] = useState<string | null>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const tip = useTipSeen('form_seeker');

  useEffect(() => {
    if (error) {
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [error]);

  const toggleVehicle = (vehicle: VehicleType) => {
    setFormData((prev) => ({
      ...prev,
      vehicle_types: prev.vehicle_types.includes(vehicle)
        ? prev.vehicle_types.filter((v) => v !== vehicle)
        : [...prev.vehicle_types, vehicle],
    }));
  };

  const toggleSecurity = (feature: string) => {
    setFormData((prev) => ({
      ...prev,
      requires_security: prev.requires_security.includes(feature)
        ? prev.requires_security.filter((f) => f !== feature)
        : [...prev.requires_security, feature],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (formData.vehicle_types.length === 0) {
      trackEvent('add_pin_validation_error', { pin_type: 'seeker', error_field: 'vehicle_types' });
      setError('Please select at least one vehicle type');
      return;
    }

    try {
      await onSubmit({
        ...formData,
        budget_min: formData.wants_free ? 0 : formData.budget_min ? parseFloat(formData.budget_min) : null,
        budget_max: formData.wants_free ? 0 : formData.budget_max ? parseFloat(formData.budget_max) : null,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to submit');
    }
  };

  return (
    <form id={id} onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-blue-50 text-blue-800 text-xs p-2 rounded-lg">
        Looking near: {lat.toFixed(4)}, {lng.toFixed(4)}
      </div>

      {!tip.seen && (
        <div className="bg-blue-50 border border-blue-200 text-blue-900 text-xs p-3 rounded-lg flex items-start gap-2" data-testid="form-tip-seeker">
          <span className="text-base leading-none">💡</span>
          <div className="flex-1">
            <div className="font-semibold mb-0.5">Finding parking</div>
            <p className="leading-relaxed text-blue-800">
              Drop this pin at the exact spot you need parking. We'll email you when a matching spot opens within 3 km. Your pin auto-expires after 30 days.
            </p>
          </div>
          <button
            type="button"
            onClick={tip.mark}
            className="text-blue-700 hover:text-blue-900 font-semibold px-1"
            aria-label="Dismiss tip"
          >
            &times;
          </button>
        </div>
      )}

      {error && (
        <div ref={errorRef} className="bg-red-50 text-red-700 text-sm p-2 rounded-lg">
          {error}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Vehicle Types</label>
        <div className="flex flex-wrap gap-2">
          {Object.entries(VEHICLE_LABELS).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => toggleVehicle(key as VehicleType)}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                formData.vehicle_types.includes(key as VehicleType)
                  ? 'bg-parksy-100 border-parksy-500 text-parksy-700'
                  : 'bg-white border-gray-300 text-gray-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Duration Needed</label>
        <select
          value={formData.duration_needed}
          onChange={(e) => setFormData({ ...formData, duration_needed: e.target.value as any })}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          {Object.entries(DURATION_LABELS).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={formData.wants_free}
          onChange={(e) => setFormData({ ...formData, wants_free: e.target.checked })}
          className="rounded border-gray-300 text-parksy-600"
        />
        <span className="text-sm font-medium text-gray-700">Looking for free parking</span>
      </label>

      <div className={`grid grid-cols-2 gap-2 ${formData.wants_free ? 'opacity-50 pointer-events-none' : ''}`}>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Min Budget (₹)</label>
          <input
            type="number"
            value={formData.wants_free ? '0' : formData.budget_min}
            onChange={(e) => setFormData({ ...formData, budget_min: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Max Budget (₹)</label>
          <input
            type="number"
            value={formData.wants_free ? '0' : formData.budget_max}
            onChange={(e) => setFormData({ ...formData, budget_max: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Security Required</label>
        <div className="flex flex-wrap gap-2">
          {Object.entries(SECURITY_LABELS).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => toggleSecurity(key)}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                formData.requires_security.includes(key)
                  ? 'bg-parksy-100 border-parksy-500 text-parksy-700'
                  : 'bg-white border-gray-300 text-gray-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.requires_covered}
            onChange={(e) => setFormData({ ...formData, requires_covered: e.target.checked })}
            className="rounded border-gray-300 text-parksy-600"
          />
          <span className="text-sm text-gray-700">Covered parking required</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.requires_24x7}
            onChange={(e) => setFormData({ ...formData, requires_24x7: e.target.checked })}
            className="rounded border-gray-300 text-parksy-600"
          />
          <span className="text-sm text-gray-700">24/7 access required</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.requires_ev}
            onChange={(e) => setFormData({ ...formData, requires_ev: e.target.checked })}
            className="rounded border-gray-300 text-parksy-600"
          />
          <span className="text-sm text-gray-700">EV charging required</span>
        </label>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Email *</label>
          <input
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone *</label>
          <input
            type="tel"
            required
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="10-digit number"
          />
        </div>
      </div>

      <p className="text-xs text-gray-500">
        We'll email you when a matching spot opens within 3 km. No spam.
      </p>
    </form>
  );
}
